import React, { useState } from 'react';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  CheckCircle, 
  Clock, 
  Play, 
  Settings, 
  Brain,
  Smartphone,
  Shield,
  Plug,
  TrendingUp,
  Users,
  FileText,
  Zap
} from 'lucide-react';
import { NavLink } from 'react-router-dom';

interface Phase {
  id: number;
  title: string;
  description: string;
  status: 'completed' | 'current' | 'upcoming';
  progress: number;
  features: string[];
  icon: React.ComponentType<any>;
  estimatedTime: string;
  dependencies?: number[];
}

const phases: Phase[] = [
  {
    id: 1,
    title: 'Foundation Setup',
    description: 'Basic infrastructure and core functionality',
    status: 'completed',
    progress: 100,
    features: ['User Authentication', 'Basic UI', 'Database Setup'],
    icon: Settings,
    estimatedTime: '2 weeks'
  },
  {
    id: 2,
    title: 'Partner & Customer Onboarding',
    description: 'Partner management and customer setup',
    status: 'completed',
    progress: 100,
    features: ['Partner Management', 'Customer Management', 'Site Management'],
    icon: Users,
    estimatedTime: '2 weeks',
    dependencies: [1]
  },
  {
    id: 3,
    title: 'Bill Processing Core',
    description: 'Core bill processing and AI extraction',
    status: 'completed',
    progress: 100,
    features: ['File Upload', 'AI Extraction', 'Processing Queue'],
    icon: FileText,
    estimatedTime: '3 weeks',
    dependencies: [2]
  },
  {
    id: 4,
    title: 'Advanced Processing',
    description: 'Enhanced AI capabilities and automation',
    status: 'completed',
    progress: 100,
    features: ['AI Agents', 'Workflow Automation', 'Quality Control'],
    icon: Brain,
    estimatedTime: '2 weeks',
    dependencies: [3]
  },
  {
    id: 5,
    title: 'Collection Management',
    description: 'Payment collection and workflow management',
    status: 'completed',
    progress: 100,
    features: ['Collection Workflows', 'Payment Plans', 'Contact Management'],
    icon: TrendingUp,
    estimatedTime: '2 weeks',
    dependencies: [4]
  },
  {
    id: 6,
    title: 'Reporting & Analytics',
    description: 'Advanced reporting and business intelligence',
    status: 'completed',
    progress: 100,
    features: ['Custom Reports', 'Analytics Dashboard', 'Data Export'],
    icon: TrendingUp,
    estimatedTime: '2 weeks',
    dependencies: [5]
  },
  {
    id: 7,
    title: 'Performance Optimization',
    description: 'System optimization and monitoring',
    status: 'completed',
    progress: 100,
    features: ['Performance Monitoring', 'Optimization Engine', 'Real-time Analytics'],
    icon: Zap,
    estimatedTime: '1 week',
    dependencies: [6]
  },
  {
    id: 8,
    title: 'AI/ML Enhancement',
    description: 'Advanced AI features and machine learning',
    status: 'completed',
    progress: 100,
    features: ['AI Learning Dashboard', 'Predictive Analytics', 'Smart Recommendations', 'Model Management'],
    icon: Brain,
    estimatedTime: '3 weeks',
    dependencies: [7]
  },
  {
    id: 9,
    title: 'Mobile & PWA',
    description: 'Mobile-first design and progressive web app',
    status: 'completed',
    progress: 100,
    features: ['Mobile UI', 'PWA Features', 'Offline Sync', 'Native Features'],
    icon: Smartphone,
    estimatedTime: '2 weeks',
    dependencies: [8]
  },
  {
    id: 10,
    title: 'Security & Compliance',
    description: 'Advanced security and compliance features',
    status: 'completed',
    progress: 100,
    features: ['Advanced Auth', 'Data Encryption', 'Compliance Dashboard', 'Audit Trail'],
    icon: Shield,
    estimatedTime: '2 weeks',
    dependencies: [9]
  },
  {
    id: 11,
    title: 'API & Integration',
    description: 'API platform and third-party integrations',
    status: 'completed',
    progress: 100,
    features: ['REST API', 'Webhook Management', 'SDK Development', 'API Documentation'],
    icon: Plug,
    estimatedTime: '3 weeks',
    dependencies: [10]
  }
];

export default function PhaseManagement() {
  const [selectedPhase, setSelectedPhase] = useState<Phase>(phases[7]); // Phase 8 (current)

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'default';
      case 'current': return 'secondary';
      case 'upcoming': return 'outline';
      default: return 'outline';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return CheckCircle;
      case 'current': return Play;
      case 'upcoming': return Clock;
      default: return Clock;
    }
  };

  const completedPhases = phases.filter(p => p.status === 'completed').length;
  const totalPhases = phases.length;
  const overallProgress = (completedPhases / totalPhases) * 100;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Phase Management Dashboard"
        description="Track development progress and manage implementation phases"
      />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Overall Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{Math.round(overallProgress)}%</div>
            <Progress value={overallProgress} className="h-2 mt-2" />
            <p className="text-xs text-muted-foreground mt-1">{completedPhases} of {totalPhases} phases</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Current Phase</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-lg font-bold">Phase {selectedPhase.id}</div>
            <p className="text-sm text-muted-foreground">{selectedPhase.title}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Features Delivered</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {phases.filter(p => p.status === 'completed').reduce((acc, p) => acc + p.features.length, 0)}
            </div>
            <p className="text-xs text-muted-foreground">Across all phases</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Est. Completion</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-lg font-bold">6 weeks</div>
            <p className="text-xs text-muted-foreground">Remaining phases</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="timeline" className="w-full">
        <TabsList>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
          <TabsTrigger value="current">Current Phase</TabsTrigger>
          <TabsTrigger value="features">Feature Map</TabsTrigger>
          <TabsTrigger value="dependencies">Dependencies</TabsTrigger>
        </TabsList>

        <TabsContent value="timeline" className="space-y-4">
          <div className="grid gap-4">
            {phases.map((phase) => {
              const StatusIcon = getStatusIcon(phase.status);
              const PhaseIcon = phase.icon;
              return (
                <Card 
                  key={phase.id} 
                  className={`cursor-pointer transition-all hover:shadow-md ${
                    selectedPhase.id === phase.id ? 'ring-2 ring-primary' : ''
                  }`}
                  onClick={() => setSelectedPhase(phase)}
                >
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 rounded-lg bg-primary/10">
                          <PhaseIcon className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">Phase {phase.id}: {phase.title}</CardTitle>
                          <CardDescription>{phase.description}</CardDescription>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge variant={getStatusColor(phase.status)} className="flex items-center space-x-1">
                          <StatusIcon className="h-3 w-3" />
                          <span>{phase.status}</span>
                        </Badge>
                        <span className="text-sm text-muted-foreground">{phase.estimatedTime}</span>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium">Progress</span>
                          <span className="text-sm text-muted-foreground">{phase.progress}%</span>
                        </div>
                        <Progress value={phase.progress} className="h-2" />
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {phase.features.map((feature, index) => (
                          <Badge key={index} variant="outline" className="text-xs">
                            {feature}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="current" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-3">
                <div className="p-3 rounded-lg bg-primary/10">
                  <selectedPhase.icon className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-xl">Phase {selectedPhase.id}: {selectedPhase.title}</CardTitle>
                  <CardDescription>{selectedPhase.description}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">Overall Progress</span>
                  <span className="text-muted-foreground">{selectedPhase.progress}%</span>
                </div>
                <Progress value={selectedPhase.progress} className="h-3" />
              </div>

              <div>
                <h4 className="font-medium mb-3">Phase Features</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {selectedPhase.features.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-2 p-3 bg-muted/50 rounded-lg">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {selectedPhase.id === 8 && (
                <div className="space-y-3">
                  <h4 className="font-medium">Quick Actions</h4>
                  <div className="flex flex-wrap gap-2">
                    <Button asChild>
                      <NavLink to="/dashboard/ai-learning">
                        <Brain className="h-4 w-4 mr-2" />
                        AI Learning Dashboard
                      </NavLink>
                    </Button>
                    <Button variant="outline" asChild>
                      <NavLink to="/dashboard/predictive-analytics">
                        <TrendingUp className="h-4 w-4 mr-2" />
                        Predictive Analytics
                      </NavLink>
                    </Button>
                    <Button variant="outline" asChild>
                      <NavLink to="/dashboard/validation-dashboard">
                        <Settings className="h-4 w-4 mr-2" />
                        Validation Engine
                      </NavLink>
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="features" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {phases.map((phase) => (
              <Card key={phase.id}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center space-x-2">
                    <phase.icon className="h-4 w-4" />
                    <span>Phase {phase.id}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {phase.features.map((feature, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <div className={`h-2 w-2 rounded-full ${
                          phase.status === 'completed' ? 'bg-green-500' :
                          phase.status === 'current' ? 'bg-blue-500' : 'bg-gray-300'
                        }`} />
                        <span className="text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="dependencies" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Phase Dependencies</CardTitle>
              <CardDescription>Understanding the relationships between different phases</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {phases.map((phase) => (
                  <div key={phase.id} className="flex items-center space-x-4 p-3 border rounded-lg">
                    <div className="flex items-center space-x-2">
                      <phase.icon className="h-4 w-4 text-primary" />
                      <span className="font-medium">Phase {phase.id}</span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {phase.dependencies && phase.dependencies.length > 0 ? (
                        <span>Depends on: Phase {phase.dependencies.join(', Phase ')}</span>
                      ) : (
                        <span>No dependencies</span>
                      )}
                    </div>
                    <Badge variant={getStatusColor(phase.status)}>
                      {phase.status}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}